import { motion, Variants } from 'framer-motion'
import { Easing } from 'framer-motion/types/types'
import React, { useCallback, useMemo, useRef } from 'react'
import styled, { css } from 'styled-components'
import { CSSCursor } from '../types/css'
import { BaseProps, BorderProps } from '../types/props'
import { getBorderStyle } from '../util/props/border'
import { overloadColor } from '../util/style'
import { isUndefined } from '../util/typechecks'

interface WrapperProps extends BorderProps {
  disabled: boolean

  width: number
  height: string

  background: string
  borderRadius: string
  shadow?: string
  cursor: CSSCursor
  noBackgroundTransition: boolean
  backgroundTransitionDuration: number
}
const Wrapper = styled.div<WrapperProps>`
  position: relative;
  width: ${props => props.width}px;
  height: ${props => props.height};

  background: ${props => overloadColor(props.background)};

  ${getBorderStyle}
  border-radius: ${props => props.borderRadius};
  box-shadow: ${props => props.shadow};
  cursor: ${props => props.cursor};

  ${props =>
    !props.noBackgroundTransition &&
    css`
      transition: background-color ${props.backgroundTransitionDuration}ms
        linear;
    `}

  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `}

  &:focus {
    outline: none;
    box-shadow: 0 0 1px 2px highlight;
  }
`
interface ThumbProps {
  disabled: boolean
  width?: number
  background: string
  borderRadius: string
  margin: number
  shadow: string
  cursor: CSSCursor
}
const Thumb = styled(motion.div)<ThumbProps>`
  position: absolute;
  width: ${props =>
    props.width ? `${props.width}px` : `calc(50% - ${props.margin}px)`};
  height: calc(100% - ${props => props.margin * 2}px);
  top: ${props => props.margin}px;

  background: ${props => overloadColor(props.background)};
  border-radius: ${props => props.borderRadius};
  background-clip: padding-box;
  box-shadow: ${props => props.shadow};
  cursor: ${props => (props.disabled ? 'not-allowed' : props.cursor)};
`

export interface ToggleProps extends BorderProps, BaseProps {
  value: boolean

  width?: number
  height?: string

  disabled?: boolean
  tabIndex?: number

  background?: string
  onBackground?: string
  offBackground?: string
  disabledBackground?: string
  borderRadius?: string
  shadow?: string
  cursor?: CSSCursor

  thumbWidth?: number
  thumbMargin?: number
  thumbColor?: string
  thumbBorderRadius?: string
  thumbShadow?: string
  thumbCursor?: CSSCursor

  noBackgroundTransition?: boolean
  backgroundTransitionDuration?: number
  noThumbAnimation?: boolean
  thumbAnimationDuration?: number
  thumbAnimationEasing?: Easing | Easing[]

  onChange: (value: boolean) => void
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void
}

export const Toggle: React.FC<ToggleProps> = ({
  className,
  id,
  value,

  disabled = false,
  tabIndex = 0,

  width = 92,
  height = '32px',

  background = 'primary',
  onBackground,
  offBackground,
  disabledBackground = 'gray7',
  border,
  borderStyle,
  borderWidth,
  borderColor,
  borderRadius = '999px',
  shadow = 'inset 1px 1px 2px rgba(0, 0, 0, 0.2)',
  cursor = 'pointer',

  thumbWidth,
  thumbMargin = 4,
  thumbColor = 'white',
  thumbBorderRadius,
  thumbShadow = '1px 1px 2px rgba(0, 0, 0, 0.2)',
  thumbCursor,

  noBackgroundTransition = false,
  backgroundTransitionDuration,
  noThumbAnimation = false,
  thumbAnimationDuration = 200,
  // ease-in-out-quad function
  thumbAnimationEasing = x =>
    x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2,

  onChange,
  onClick = () => void 0,
  onKeyDown = () => void 0,
}) => {
  const thumbRef = useRef<HTMLDivElement | null>(null)
  const draggingRef = useRef(false)

  const parsedThumbWidth = useMemo(() => {
    if (!isUndefined(thumbWidth)) return thumbWidth
    if (!thumbRef.current) return width / 2 - thumbMargin
    return thumbRef.current.offsetWidth
  }, [thumbMargin, thumbWidth, width])

  const emitChange = useCallback(
    (val: boolean) => {
      onChange(val)
    },
    [onChange]
  )

  const handleClick = useCallback(
    (evt: React.MouseEvent<HTMLDivElement>) => {
      if (draggingRef.current || disabled) return

      onClick(evt)
      if (evt.defaultPrevented) return

      emitChange(!value)
    },
    [onClick, emitChange, value, disabled]
  )

  const handleKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return

      onKeyDown(evt)
      if (evt.defaultPrevented) return

      const { key } = evt
      if (key === 'ArrowRight' && !value) emitChange(true)
      else if (key === 'ArrowLeft' && value) emitChange(false)
      else if (key === 'Enter' || key === ' ') emitChange(!value)
    },
    [value, disabled, emitChange, onKeyDown]
  )

  const variants = useMemo<Variants>(
    () => ({
      on: {
        left: width - thumbMargin - parsedThumbWidth,
        translateX: 0,
      },
      off: {
        left: thumbMargin,
        translateX: 0,
      },
    }),
    [parsedThumbWidth, thumbMargin, width]
  )

  const wrapperBackground = useMemo(() => {
    if (disabled) return disabledBackground ?? background
    if (value) return onBackground ?? background
    else return offBackground ?? background
  }, [
    value,
    disabled,
    background,
    onBackground,
    offBackground,
    disabledBackground,
  ])

  return (
    <Wrapper
      className={className}
      id={id}
      disabled={disabled}
      tabIndex={tabIndex}
      width={width}
      height={height}
      background={wrapperBackground}
      border={border}
      borderWidth={borderWidth}
      borderStyle={borderStyle}
      borderColor={borderColor}
      borderRadius={borderRadius}
      shadow={shadow}
      cursor={cursor}
      noBackgroundTransition={noBackgroundTransition}
      backgroundTransitionDuration={
        backgroundTransitionDuration ?? thumbAnimationDuration * 0.75
      }
      onKeyDown={handleKeyDown}
      onClick={handleClick}
    >
      <Thumb
        className="--toggle-thumb"
        variants={variants}
        initial={false}
        animate={value ? 'on' : 'off'}
        transition={
          noThumbAnimation
            ? { duration: 0 }
            : {
                duration: thumbAnimationDuration / 1000,
                ease: thumbAnimationEasing,
              }
        }
        disabled={disabled}
        width={thumbWidth}
        margin={thumbMargin}
        background={thumbColor}
        borderRadius={thumbBorderRadius ?? borderRadius}
        shadow={thumbShadow}
        cursor={thumbCursor ?? cursor}
      />
    </Wrapper>
  )
}
